import Markdown, { Components } from 'react-markdown';
import remarkDirective from "remark-directive";
import { visit } from "unist-util-visit";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import rehypeRaw from "rehype-raw";
import type { PluggableList } from "unified";
import { Dropdown } from "./Dropdown";
import { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Field, FieldProps } from './Field';
import { ContinueJourneyButton, ContinueJourneyButtonProps, OpenTaskButton, OpenTaskButtonProps } from './OpenTaskButton';
import { useChapterHandler } from '@/contexts/chapterHandler';
import { useStepHandler } from '@/contexts/stepHandler';
import { ScrollTargetId, useScroller } from '@/hooks/useScroller';
import { TaskTextarea, TaskTextareaProps } from './ChapterCard/TaskTextarea';

const simpleTextDirectives = [
    "section-title",
    "chapter-title",
    "step-number",
    "chapter-number",
    "section-number",
] as const;

type ExtraComponents = Components & {
    dropdown?: (props: { title: string; children: ReactNode; }) => ReactNode;
    field?: (props: FieldProps) => ReactNode;
    // using "textarea" as the key caused error at implementation
    "task-textarea"?: (props: TaskTextareaProps) => ReactNode;
    "open-task-button"?: (props: OpenTaskButtonProps) => ReactNode;
    "continue-journey-button"?: (props: ContinueJourneyButtonProps) => ReactNode;
    "scroll-target"?: ({ targetId }: { targetId: ScrollTargetId }) => ReactNode;
    "tts-segment"?: (props: { index: number; children?: ReactNode; }) => ReactNode;
} & {
    [key in typeof simpleTextDirectives[number]]?: () => ReactNode;
};

export function MarkdownH3({ children, className }: { children: ReactNode, className?: string }) {
    return (
        <p children={children} className={cn("pt-2 text-step-current", className)} />
    );
}

const components = ({ textColor: color }: { textColor: string }): ExtraComponents => ({
    dropdown: Dropdown,
    field(props) { return <Field {...props} />; }, //placeholder. Details should be implemented where the Markdown component is used
    "task-textarea"(props) { return <TaskTextarea {...props} />; }, //placeholder. Details should be implemented where the Markdown component is used
    "open-task-button": (props) => <OpenTaskButton {...props} />,
    "continue-journey-button": (props) => <ContinueJourneyButton {...props} />,
    p(props) {
        return <p children={props.children} className='pt-2 text-base' />;
    },
    h1(props) {
        return (
            <h1 className={cn("text-xl text-step-current", color)} children={props.children} />
        );
    },
    h2(props) {
        return (
            <>
                <br />
                <h2 className={cn("text-step-current", color)} children={props.children} />
            </>
        );
    },
    h3(props) {
        return (
            <MarkdownH3 children={props.children} className={color} />
        );
    },
    ul(props) {
        return (
            <ul className='list-disc list-inside' children={props.children} />
        );
    },
    ol(props) {
        return (
            <ol className='list-decimal list-inside' children={props.children} />
        );
    },
});

const schema = {
    ...defaultSchema,
    clobberPrefix: "md-",
    tagNames: [
        ...(defaultSchema.tagNames || []),
        ...simpleTextDirectives,
        "input",
        "dropdown",
        "field",
        "task-textarea",
        "open-task-button",
        "continue-journey-button",
        "section-title",
        "scroll-target",
        "tts-segment"
    ],
    attributes: {
        ...defaultSchema.attributes,
        dropdown: ["title"], // allow title prop for your component
        input: ["type"],
        field: ["type", "label", "id"],
        "task-textarea": ["label", "id", "lines"],
        "open-task-button": ["task", "label", "scrollTarget"],
        "continue-journey-button": ["label"],
        "scroll-target": ["targetId"],
        "tts-segment": ["index"],
    },
};

const remarkDirectivePlugin = remarkDirective as unknown;
const remarkPlugins: PluggableList = [
    remarkDirectivePlugin,
    remarkDropdown,
    remarkCheckbox,
    remarkLeafDirectives,
    remarkTextDirectives
];

const rehypePlugins: PluggableList = [rehypeRaw, [rehypeSanitize, schema]];

function remarkDropdown() {
    return (tree) => {
        visit(tree, (node) => {
            if (node.type === "containerDirective" && node.name === "dropdown") {
                const data = node.data || (node.data = {});

                // Default title
                let title = "Details";

                // If there's a bracket child, remark-directive puts it in node.children[0]
                if (node.children?.length) {
                    const firstChild = node.children[0];
                    if (firstChild.type === "paragraph" && firstChild.children?.[0]?.value) {
                        title = firstChild.children[0].value;
                        // Remove it from content so it doesn't appear twice
                        node.children.shift();
                    }
                }

                data.hName = "dropdown";
                data.hProperties = { title };
            }
        });
    };
}

function remarkCheckbox() {
    return (tree) => {
        visit(tree, (node) => {
            if (node.type !== "leafDirective" || node.name !== "checkbox") return;

            const data = node.data || (node.data = {});
            let label = "";
            if (node.children?.length) {
                label = node.children[0]?.value;
                node.children.shift();
            }
            data.hName = "field";
            data.hProperties = { type: "checkbox", label, id: node.attributes.id };
        });
    };
}

function remarkTextDirectives() {
    return (tree) => {
        visit(tree, (node) => {
            if (node.type !== "textDirective") return;

            const data = node.data || (node.data = {});

            if (simpleTextDirectives.includes(node.name)) {
                data.hName = node.name;
            } else if (node.name === "scroll-target") {
                data.hName = node.name;
                data.hProperties = { id: node.attributes.id };
            }
        });
    };
}

function remarkLeafDirectives() {
    return (tree) => {
        visit(tree, (node) => {
            if (node.type !== "leafDirective") return;

            const data = node.data || (node.data = {});

            if (node.name === "scroll-target") {
                data.hName = node.name;
                data.hProperties = { targetId: node.attributes.targetId };
            } else if (node.name === "open-task-button") {
                let label = "";
                if (node.children?.length) {
                    label = node.children[0]?.value;
                    node.children.shift();
                }
                data.hName = "open-task-button";
                data.hProperties = {
                    label,
                    scrollTarget: node.attributes.scrollTarget,
                    task: node.attributes.task,
                };
            } else if (node.name === "continue-journey-button") {
                let label = "";
                if (node.children?.length) {
                    label = node.children[0]?.value;
                    node.children.shift();
                }
                data.hName = "continue-journey-button";
                data.hProperties = {
                    label,
                };
            } else if (node.name === "textarea") {
                let label = "";
                if (node.children?.length) {
                    label = node.children[0]?.value;
                    node.children.shift();
                }
                data.hName = "task-textarea";
                data.hProperties = {
                    label,
                    id: node.attributes.id,
                    lines: node.attributes.lines,
                };
            }
        });
    };
}

type TaskMarkdownProps = {
    children: string;
    paragraphSpacing?: string | number;
    className?: string;
    components?: ExtraComponents;
    textColor?: string;
    /** By default, warnings are shown if this component is not inside of context providers that are required for some directives to work correctly.
     * This lets you suppress those warnings.
     */
    suppressProviderWarnings?: boolean;
    /** Additional remark plugins appended after the built-in ones (e.g. to opt a specific call site into TTS segmentation). */
    extraRemarkPlugins?: PluggableList;
};
export function TaskMarkdown({ textColor, suppressProviderWarnings, children, paragraphSpacing, className, components: propComponents, extraRemarkPlugins }: TaskMarkdownProps) {
    const { currentSection, chapter: currentChapter } = useChapterHandler() ?? {};
    const { currentStep } = useStepHandler() ?? {};
    const { createTarget } = useScroller();

    if (!suppressProviderWarnings) {
        if (!currentSection || !currentChapter) console.warn("TaskMarkdown is being used outside of a ChapterHandlerProvider. Some directives might not work correctly.");
        if (!currentStep) console.warn("TaskMarkdown is being used outside of a StepHandlerProvider. Some directives might not work correctly.");
    }

    return (
        <div
            className={cn("flex flex-col", className)}
            style={{ gap: paragraphSpacing }}
        >
            <Markdown
                remarkPlugins={extraRemarkPlugins?.length ? [...remarkPlugins, ...extraRemarkPlugins] : remarkPlugins}
                rehypePlugins={rehypePlugins}
                components={{
                    ...components({ textColor }),
                    ...propComponents,
                    "chapter-title": () => <MiniMarkdown>{currentChapter?.title}</MiniMarkdown>,
                    "section-title": () => <MiniMarkdown>{currentSection?.section.title}</MiniMarkdown>,
                    "step-number": () => currentStep?.number,
                    "chapter-number": () => currentChapter?.number,
                    "section-number": () => currentSection?.section.number,
                    "scroll-target": ({ targetId }) => createTarget(targetId),
                } as ExtraComponents}
            >
                {children}
            </Markdown>
        </div>
    );
}

const MiniMarkdown = ({ children }) => <Markdown components={{ p: (props) => props.children }} children={children} />;